import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';
import { toDate } from '../../utils/lotTiming';
import { useSaleSelection } from '../../hooks/useSaleSelection';
import SaleContextBanner from '../../components/admin/SaleContextBanner';

/**
 * ADMIN — Bid Manager. AdminRoute-gated (renders bidder PII + bid amounts).
 * Mirrors SaleRegistrations: pick an auction, load its `bids`, join `users`, table by lot.
 *
 * VIEW is REAL (read-only): see every bid on every lot — bidder, amount, time, status —
 * to spot error bids and bad-actor patterns.
 *
 * EDIT/REMOVE are STUBS — the UI is present but writes NOTHING. Firestore rules already
 * deny all client bid update/delete (bids.update=false, delete=false), so mutation must
 * go through a server path anyway.
 *
 * ⚠️ GUARDRAILS FOR WHEN REMOVAL IS WIRED (design the shell around these — do NOT ever
 * build a raw silent delete):
 *   1. SOFT-DELETE, never hard-delete — void the bid (set status:'rejected' + voidReason +
 *      voidedAt), keep the doc so it's recoverable and the trail survives.
 *   2. REASON REQUIRED — no removal without a stated why (this stub already enforces that:
 *      the Remove button stays disabled until a reason is entered).
 *   3. AUDITED — record who (admin uid), when (server time), which bid, prior status, and
 *      the reason. This is the most integrity-sensitive action in the system (thumb-on-the-
 *      scale power); it must be attributable and reversible.
 *   Wire via an isPaymentStaff-gated Cloud Function (Admin SDK) writing a bidAudit record +
 *      the soft-delete, then re-run the outbid resolution. Never a client write.
 */

interface BidRow {
  id: string;
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  bidderId: string;
  bidderName: string;
  bidderNumber: string;
  email: string;
  amount: number; // cents
  bidType: string;
  isAuto: boolean;
  isWinning: boolean;
  pending: boolean; // proposed, not yet granted/refused by the trigger
  voided: boolean;
  voidReason?: string;
  placedAt: Date | null;
}

interface LotGroup {
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  bids: BidRow[];
  activeCount: number;
  distinctBidders: number;
  highActive: number;
}

const money = (cents?: number) => `$${(((cents ?? 0)) / 100).toFixed(2)}`;
/*
 * A private toDate used to live here. It handled a Firestore Timestamp and an
 * ISO string but NOT the plain { seconds, nanoseconds } shape a Timestamp
 * degrades to when it loses its prototype — utils/lotTiming's version does, and
 * saleState was already brought in line with it. Three copies of a date parser
 * with three different tolerances is how a lot silently reads as having no end
 * time. Imported now, not redeclared.
 */
const isVoided = (b: any) => b.status === 'rejected' || !!b.voidReason;
const isAuto = (b: any) => b.bidType === 'proxy-auto' || b.systemGenerated === true;

const AdminBids: React.FC = () => {
  const [rows, setRows] = useState<BidRow[]>([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [filter, setFilter] = useState('');
  const [showVoided, setShowVoided] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // STUB modal — preview only, writes nothing.
  const [act, setAct] = useState<{ bid: BidRow; mode: 'remove' | 'edit' } | null>(null);
  const [reason, setReason] = useState('');
  const [newAmount, setNewAmount] = useState('');

  /*
   * This page had its own auction loader that sorted client-side on `endDate`
   * desc — a THIRD definition of "the current sale", differing from the
   * createdAt-desc used by the Picker and friends and from the hardcoded id in
   * the Shipping Station. It was also the wrong key on its own terms: `endDate`
   * is the SCHEDULED end, and soft close pushes the real close past it.
   *
   * Role 'current' rather than 'fulfillment': the Bid Manager is about the sale
   * in play, so it SHOULD follow a live sale — unlike the Picker and Shipping.
   */
  const selection = useSaleSelection({ role: 'current', storageKey: 'adminBids' });
  const { saleId: auctionId } = selection;

  // Load bids for the selected auction + join users for email/number.
  useEffect(() => {
    let active = true;
    if (!auctionId) return;
    setLoadingBids(true);
    (async () => {
      try {
        const [aSnap, bidsSnap] = await Promise.all([
          getDoc(doc(db, 'auctions', auctionId)),
          getDocs(query(collection(db, 'bids'), where('auctionId', '==', auctionId), orderBy('placedAt', 'desc'))),
        ]);
        if (!active) return;

        const lotMeta: Record<string, { lotNumber?: string; title?: string }> = {};
        if (aSnap.exists()) {
          (aSnap.data() as any).lots?.forEach((l: any) => { lotMeta[l.id] = { lotNumber: l.lotNumber, title: l.title }; });
        }

        const raw = bidsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));

        // Join users for email/bidderNumber (distinct bidderIds).
        const uids = Array.from(new Set(raw.map((b) => b.bidderId).filter(Boolean)));
        const userEntries = await Promise.all(uids.map(async (uid) => {
          try { const u = await getDoc(doc(db, 'users', uid)); return [uid, u.exists() ? (u.data() as any) : {}] as const; }
          catch { return [uid, {}] as const; }
        }));
        const userMap = new Map(userEntries);
        if (!active) return;

        const out: BidRow[] = raw.map((b) => {
          const u: any = userMap.get(b.bidderId) || {};
          const meta = lotMeta[b.lotId] || {};
          return {
            id: b.id,
            lotId: b.lotId,
            lotNumber: b.lotNumber || meta.lotNumber || '—',
            lotTitle: b.lotTitle || meta.title || 'Lot',
            bidderId: b.bidderId,
            bidderName: b.bidderName || u.displayName || '—',
            bidderNumber: b.bidderNumber || u.bidderNumber || '—',
            email: u.email || '—',
            amount: b.amount ?? 0,
            bidType: b.bidType || 'manual',
            isAuto: isAuto(b),
            isWinning: !!b.isWinning && !isVoided(b),
            pending: b.status === 'pending' && !isVoided(b),
            voided: isVoided(b),
            voidReason: b.voidReason,
            placedAt: toDate(b.placedAt),
          };
        });
        setRows(out);
      } catch (e) {
        console.error('Failed to load bids:', e);
        toast.error('Could not load bids for this sale.');
        setRows([]);
      } finally {
        if (active) setLoadingBids(false);
      }
    })();
    return () => { active = false; };
  }, [auctionId]);

  // Filter + group by lot.
  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (!showVoided && r.voided) return false;
      if (!q) return true;
      return (
        r.bidderName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        String(r.bidderNumber).toLowerCase().includes(q) ||
        String(r.lotNumber).toLowerCase().includes(q) ||
        r.bidderId.toLowerCase().includes(q)
      );
    });
    const map = new Map<string, LotGroup>();
    for (const r of filtered) {
      let g = map.get(r.lotId);
      if (!g) { g = { lotId: r.lotId, lotNumber: r.lotNumber, lotTitle: r.lotTitle, bids: [], activeCount: 0, distinctBidders: 0, highActive: 0 }; map.set(r.lotId, g); }
      g.bids.push(r);
    }
    const list = Array.from(map.values());
    list.forEach((g) => {
      const active = g.bids.filter((b) => !b.voided);
      g.activeCount = active.length;
      g.distinctBidders = new Set(active.map((b) => b.bidderId)).size;
      g.highActive = active.reduce((m, b) => Math.max(m, b.amount), 0);
      g.bids.sort((a, b) => b.amount - a.amount || (b.placedAt?.getTime() ?? 0) - (a.placedAt?.getTime() ?? 0));
    });
    list.sort((a, b) => {
      const an = parseInt(a.lotNumber) || 0, bn = parseInt(b.lotNumber) || 0;
      return an - bn;
    });
    return list;
  }, [rows, filter, showVoided]);

  const totalBids = rows.length;
  const activeBids = rows.filter((r) => !r.voided).length;
  const voidedBids = totalBids - activeBids;

  const openAct = (bid: BidRow, mode: 'remove' | 'edit') => {
    setReason('');
    setNewAmount(mode === 'edit' ? (bid.amount / 100).toFixed(2) : '');
    setAct({ bid, mode });
  };

  // STUB: writes NOTHING. Real removal = audited, reasoned, soft-delete via Cloud Function.
  const submitStub = () => {
    toast('Preview only — not enforced yet. No bid was changed.', { icon: '🚧' });
    setAct(null);
  };

  return (
    <div className="space-y-6">
      {/* Sale in view, selector, and the stale-sale warning. */}
      <SaleContextBanner selection={selection} label="Bid Manager" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Bid Manager</h1>
          <p className="text-gray-600">
            {loadingBids ? 'Loading bids…' : `${totalBids} bids · ${activeBids} active · ${voidedBids} voided`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="input py-2"
            placeholder="Search bidder / email / lot # / UID"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="flex items-center gap-1 text-sm text-gray-600 px-2">
            <input type="checkbox" checked={showVoided} onChange={(e) => setShowVoided(e.target.checked)} />
            Show voided
          </label>
          <Link to="/admin/bidders" className="btn-secondary">Bidders →</Link>
        </div>
      </div>

      {/* Honesty banner. */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 text-sm text-amber-900">
        🚧 <strong>Editing is a preview.</strong> “Remove bid” / “Edit bid” show the intended controls but do not
        change anything yet. When wired, removal will be a <strong>reasoned, audited soft-delete</strong> (recoverable,
        never a silent hard-delete) — the most integrity-sensitive action in the system.
      </div>

      {loadingBids ? (
        <div className="text-center py-12 text-gray-600">Loading bids…</div>
      ) : groups.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          {totalBids === 0 ? 'No bids on this sale yet.' : `No bids match “${filter}”.`}
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const isCollapsed = !!collapsed[g.lotId];
            return (
              <div key={g.lotId} className="card">
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [g.lotId]: !c[g.lotId] }))}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-gray-400">{isCollapsed ? '▶' : '▼'}</span>
                    <span className="font-mono text-xs text-gray-500 whitespace-nowrap">LOT {g.lotNumber}</span>
                    <span className="font-semibold text-gray-800 truncate">{g.lotTitle}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 whitespace-nowrap">
                    <span>{g.activeCount} bids</span>
                    <span>· {g.distinctBidders} bidders</span>
                    <span className="font-bold text-gray-900">high {money(g.highActive)}</span>
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b bg-gray-50">
                          <th className="py-2 px-2">Bidder</th>
                          <th className="py-2 px-2">Email</th>
                          <th className="py-2 px-2 text-right">Amount</th>
                          <th className="py-2 px-2">When</th>
                          <th className="py-2 px-2">Status</th>
                          <th className="py-2 px-2">Manage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.bids.map((b) => (
                          <tr key={b.id} className={`border-b last:border-0 ${b.voided ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                            <td className="py-2 px-2">
                              <span className="font-semibold">{b.bidderName}</span>{' '}
                              <span className="font-mono text-xs text-gray-500">#{b.bidderNumber}</span>
                            </td>
                            <td className="py-2 px-2 text-gray-700 break-all">{b.email}</td>
                            <td className={`py-2 px-2 text-right font-bold ${b.voided ? 'line-through text-gray-400' : ''}`}>{money(b.amount)}</td>
                            <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{b.placedAt ? b.placedAt.toLocaleString() : '—'}</td>
                            <td className="py-2 px-2 whitespace-nowrap">
                              {b.voided ? (
                                <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded" title={b.voidReason || ''}>voided</span>
                              ) : b.pending ? (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded" title="Written by the bidder, not yet granted or refused by the server">pending</span>
                              ) : b.isWinning ? (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">high</span>
                              ) : (
                                <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">outbid</span>
                              )}
                              {b.isAuto && <span className="ml-1 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">auto</span>}
                            </td>
                            <td className="py-2 px-2">
                              {!b.voided && (
                                <div className="flex gap-1">
                                  <button type="button" onClick={() => openAct(b, 'edit')} className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100" title="Edit bid (preview — not enforced)">Edit</button>
                                  <button type="button" onClick={() => openAct(b, 'remove')} className="text-xs px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50" title="Remove bid (preview — not enforced)">Remove</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- STUB edit/remove modal (writes nothing; Remove requires a reason) ---- */}
      {act && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold">{act.mode === 'remove' ? 'Remove bid' : 'Edit bid'}</h2>
              <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">PREVIEW · NOT ENFORCED</span>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              LOT {act.bid.lotNumber} · {act.bid.lotTitle}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {act.bid.bidderName} (#{act.bid.bidderNumber}) · {money(act.bid.amount)} · {act.bid.placedAt ? act.bid.placedAt.toLocaleString() : ''}
            </p>

            {act.mode === 'edit' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Corrected amount (USD)</label>
                <input type="number" min="0" inputMode="decimal" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-600">*</span> <span className="text-xs text-gray-400">(required — recorded in the audit trail)</span>
            </label>
            <textarea
              rows={3}
              placeholder={act.mode === 'remove' ? 'Why is this bid being removed? (e.g. error bid, retracted, bad actor)' : 'Why is this bid being corrected?'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              When enforcement is wired: this will be a <strong>soft-delete</strong> (recoverable), stamped with your admin
              account, the time, the target bid, and this reason. No silent deletes — ever.
            </p>

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={submitStub}
                disabled={!reason.trim() || (act.mode === 'edit' && !newAmount.trim())}
                className={`flex-1 px-3 py-2 text-white text-sm rounded disabled:opacity-40 disabled:cursor-not-allowed ${act.mode === 'remove' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}`}
              >
                {act.mode === 'remove' ? 'Remove bid' : 'Save correction'}
              </button>
              <button type="button" onClick={() => setAct(null)} className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBids;
